import {
  DEFAULT_LESSON_TYPES_SETTING,
  getLessonTypeById,
  normalizeLessonTypesSetting,
} from "../common/lessonTypes";
import {
  calculateDatesBetween,
  formatDate,
  minutesToTimeString,
  timeStringToMinutes,
} from "../common/Helpers";
import { ATTENDANCE_STATUS, isAttendanceChecked } from "./lessonAttendanceStatus";

const toLessonTypesSetting = (lessonTypes = DEFAULT_LESSON_TYPES_SETTING) => {
  if (Array.isArray(lessonTypes)) {
    return normalizeLessonTypesSetting({
      id: "lessonTypes",
      types: lessonTypes.map(type => ({
        classTypeId: type.classTypeId || type.id,
        category: type.category,
        name: type.name,
        durationMinutes: type.durationMinutes || type.length,
        minStudents: type.minStudents,
        maxStudents: type.maxStudents,
        status: type.status || (type.active === false ? "INACTIVE" : "ACTIVE"),
      })),
    });
  }

  return normalizeLessonTypesSetting(lessonTypes);
};

const getClassTypeLength = (classTypeId, lessonTypes = DEFAULT_LESSON_TYPES_SETTING) => {
  const classType = getLessonTypeById(toLessonTypesSetting(lessonTypes), classTypeId);
  return classType.durationMinutes || null;
};

export const getClassEndTime = (schoolClass, lessonTypes = DEFAULT_LESSON_TYPES_SETTING) => {
  const startTimeMinutes = timeStringToMinutes(schoolClass.startTime);
  const classTypeLength = getClassTypeLength(schoolClass.classTypeId, lessonTypes);

  if (startTimeMinutes === null || classTypeLength === null) {
    return null;
  }

  return minutesToTimeString(startTimeMinutes + classTypeLength);
};

const datesOverlap = (from1, till1, from2, till2) => (
  from1 <= till2 && from2 <= till1
);

const timesOverlap = (from1, till1, from2, till2) => (
  (from1 >= from2 && till2 > from1) ||
  (till1 > from2 && till2 >= till1) ||
  (from1 <= from2 && till1 >= till2)
);

const getDateRangeIntersection = (from1, till1, from2, till2) => {
  const intersectionFromDate = from1 > from2 ? from1 : from2;
  const intersectionTillDate = till1 < till2 ? till1 : till2;

  if (intersectionFromDate > intersectionTillDate) {
    return undefined;
  }

  return {
    intersectionFromDate,
    intersectionTillDate,
  };
};

const hasClassOccurrenceInRange = (fromDate, tillDate, dayOfWeek) => (
  calculateDatesBetween(fromDate, tillDate, dayOfWeek).length > 0
);

export const getFutureEffectiveRange = ({ fromDate, tillDate }, today = formatDate(new Date())) => {
  if (!fromDate || !tillDate) {
    return undefined;
  }

  const effectiveFromDate = fromDate > today ? fromDate : today;
  if (effectiveFromDate > tillDate) {
    return undefined;
  }

  return {
    effectiveFromDate,
    effectiveTillDate: tillDate,
  };
};

export const findOverlapSchoolClass = (schoolClass, schoolClasses, today = formatDate(new Date()), lessonTypes = DEFAULT_LESSON_TYPES_SETTING) => {
  const {
    id,
    teacherId,
    dayOfWeek,
    startTime,
    classTypeId,
    fromDate,
    tillDate,
  } = schoolClass;

  if (!teacherId || !dayOfWeek || !startTime || !classTypeId || !fromDate || !tillDate) {
    return undefined;
  }

  const effectiveRange = getFutureEffectiveRange({ fromDate, tillDate }, today);
  if (!effectiveRange) {
    return undefined;
  }

  const endTime = getClassEndTime(schoolClass, lessonTypes);
  if (!endTime) {
    return undefined;
  }

  return schoolClasses.find((currentSchoolClass) => {
    if (currentSchoolClass.id === id) {
      return false;
    }
    if (
      currentSchoolClass.teacherId !== teacherId ||
      currentSchoolClass.dayOfWeek !== dayOfWeek ||
      !currentSchoolClass.fromDate ||
      !currentSchoolClass.tillDate ||
      !currentSchoolClass.startTime ||
      !currentSchoolClass.classTypeId
    ) {
      return false;
    }

    const currentEffectiveRange = getFutureEffectiveRange(
      { fromDate: currentSchoolClass.fromDate, tillDate: currentSchoolClass.tillDate },
      today,
    );
    if (!currentEffectiveRange) {
      return false;
    }

    const currentEndTime = getClassEndTime(currentSchoolClass, lessonTypes);
    if (!currentEndTime) {
      return false;
    }

    const overlapRange = getDateRangeIntersection(
      effectiveRange.effectiveFromDate,
      effectiveRange.effectiveTillDate,
      currentEffectiveRange.effectiveFromDate,
      currentEffectiveRange.effectiveTillDate,
    );
    if (!overlapRange) {
      return false;
    }

    return (
      datesOverlap(
        effectiveRange.effectiveFromDate,
        effectiveRange.effectiveTillDate,
        currentEffectiveRange.effectiveFromDate,
        currentEffectiveRange.effectiveTillDate,
      ) &&
      timesOverlap(
        startTime,
        endTime,
        currentSchoolClass.startTime,
        currentEndTime,
      ) &&
      hasClassOccurrenceInRange(
        overlapRange.intersectionFromDate,
        overlapRange.intersectionTillDate,
        dayOfWeek,
      )
    );
  });
};

export const hasScheduleChangeRequiringAttendanceGuard = (originalSchoolClass, schoolClass) => (
  !!originalSchoolClass &&
  !!schoolClass?.id &&
  (
    originalSchoolClass.startTime !== schoolClass.startTime ||
    originalSchoolClass.dayOfWeek !== schoolClass.dayOfWeek
  )
);

export const getPastLessonsWithoutAttendance = (
  scheduledLessons,
  today = formatDate(new Date()),
) => scheduledLessons.filter(lesson => (
  lesson?.defaultLessonDetails?.date < today &&
  !isAttendanceChecked(lesson)
));

export const buildNotHeldLessonRegister = (lesson) => ({
  lessonId: lesson.id || lesson.lessonId,
  lessonDetails: {
    ...lesson.defaultLessonDetails,
    attendanceStatus: ATTENDANCE_STATUS.CHECKED,
  },
  students: lesson.defaultStudents.map(({ id }) => ({ id, isPresent: false })),
});

const rollbackCreatedLessonRegisters = async (createdLessonRegisters, deleteLessonRegister) => {
  if (!deleteLessonRegister) {
    return;
  }

  await createdLessonRegisters.reduce(
    (rollbackPromise, lessonRegister) => rollbackPromise.then(() => (
      deleteLessonRegister(lessonRegister).catch(() => {
        // Best-effort rollback: preserve the original backfill failure for the caller.
      })
    )),
    Promise.resolve(),
  );
};

export const saveSchoolClassWithAttendanceGuard = async ({
  originalSchoolClass,
  schoolClass,
  scheduledLessons,
  saveLessonRegister,
  deleteLessonRegister,
  saveSchoolClass,
  confirmAttendanceBackfill,
}) => {
  const requiresAttendanceGuard = hasScheduleChangeRequiringAttendanceGuard(originalSchoolClass, schoolClass);

  if (!requiresAttendanceGuard) {
    return saveSchoolClass(schoolClass);
  }

  const pastLessonsWithoutAttendance = getPastLessonsWithoutAttendance(scheduledLessons);
  if (pastLessonsWithoutAttendance.length === 0) {
    return saveSchoolClass(schoolClass);
  }

  const shouldBackfillAttendance = await confirmAttendanceBackfill();
  if (!shouldBackfillAttendance) {
    return saveSchoolClass(schoolClass);
  }

  const createdLessonRegisters = [];
  try {
    await pastLessonsWithoutAttendance.reduce(
      (backfillPromise, lesson) => backfillPromise.then(() => (
        saveLessonRegister(buildNotHeldLessonRegister(lesson)).then((savedLessonRegister) => {
          createdLessonRegisters.push(savedLessonRegister);
        })
      )),
      Promise.resolve(),
    );
  } catch (error) {
    await rollbackCreatedLessonRegisters(createdLessonRegisters, deleteLessonRegister);
    throw error;
  }

  try {
    return await saveSchoolClass(schoolClass);
  } catch (error) {
    await rollbackCreatedLessonRegisters(createdLessonRegisters, deleteLessonRegister);
    throw error;
  }
};
